import { LocalDate } from '@js-joda/core';
import { useCallback, useEffect, useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { Range } from '@dreryk/watermelon';

import { MAX_LEAVE_DAYS, MIN_LEAVE_DAYS } from '../../common.constants';
import { MedicalLeaveCommonFormController } from '../../common.types';
import { getTodayDate } from '../../common.utils';

import { areDatesAboveMaxLeaveDays, findStartDate, getDatesDaysDiff, isDateRangeClosed } from './leaveDateRange.utils';

const useMedicalLeaveEndDateAdjuster = (controller: MedicalLeaveCommonFormController): (() => void) => {
   return useCallback(() => {
      const dateRange = controller.watch('leaveDateRange');
      if (isDateRangeClosed(dateRange)) {
         const startDate = dateRange!.lowerEndpoint();
         const rangeIsAboveMaxLeaveDays = areDatesAboveMaxLeaveDays(startDate, dateRange!.upperEndpoint());
         if (rangeIsAboveMaxLeaveDays) {
            controller.setValue(
               'leaveDateRange',
               Range.closed<LocalDate>(startDate, LocalDate.from(startDate).plusDays(MAX_LEAVE_DAYS - 1))
            );
         }
      }
   }, [controller]);
};

const useLeaveDurationDaysDiff = (controller: MedicalLeaveCommonFormController): number | null => {
   const dateRange = controller.watch('leaveDateRange');
   return useMemo(() => {
      if (!dateRange || !isDateRangeClosed(dateRange)) {
         return null;
      }
      return getDatesDaysDiff(dateRange.lowerEndpoint(), dateRange.upperEndpoint()) + 1;
   }, [dateRange]);
};

const useLeaveDaysCountControls = (controller: MedicalLeaveCommonFormController): void => {
   const durationDaysDiff = useLeaveDurationDaysDiff(controller);
   const leaveDaysCount = controller.watch('leaveDaysCount');

   useEffect(() => {
      controller.setValue('leaveDaysCount', durationDaysDiff);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [durationDaysDiff]);

   const updateDateRange = useCallback(
      (value: number): void => {
         const currentDateRange = controller.getValue('leaveDateRange');
         const dateFrom = findStartDate(currentDateRange) || getTodayDate();
         controller.setValue(
            'leaveDateRange',
            Range.closed<LocalDate>(dateFrom, LocalDate.from(dateFrom).plusDays(value - 1)),
            { shouldDirty: false, shouldValidate: true }
         );
      },
      [controller]
   );

   useUpdateEffect(() => {
      if (leaveDaysCount === null) {
         controller.setValue('leaveDaysCount', MIN_LEAVE_DAYS);
      }
   }, [leaveDaysCount]);

   useUpdateEffect(() => {
      if (leaveDaysCount && leaveDaysCount >= MIN_LEAVE_DAYS && leaveDaysCount <= MAX_LEAVE_DAYS) {
         updateDateRange(leaveDaysCount);
      }
   }, [leaveDaysCount]);
};

export { useLeaveDaysCountControls, useMedicalLeaveEndDateAdjuster };
